import React, {useEffect} from 'react';
import {MutationCache, QueryClient} from '@tanstack/react-query';
import {PersistQueryClientProvider} from '@tanstack/react-query-persist-client';
import {createAsyncStoragePersister} from '@tanstack/query-async-storage-persister';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {SafeAreaProvider} from 'react-native-safe-area-context';

import {onMustChangePassword, onUnauthorized} from '@/shared/api';
import {useAuthStore} from '@/entities/session/auth-store';
import {messageOf} from '@/shared/lib/errors';
import {toast} from '@/shared/ui/toast';
import {CACHE_VERSION} from '@/shared/config/cache-version';
// Side-effect import: i18next must be initialised before any screen calls useTranslation.
// It starts on the DEVICE language; a language the user picked before is restored a
// tick later by applyStoredLang() below, because reading it is a disk hit and blocking
// the first frame on one — to avoid a single frame in the wrong language — is the wrong
// trade on a cold start.
import '@/shared/config/i18n';
import i18n, {applyStoredLang} from '@/shared/config/i18n';

/**
 * The one place the app is wired together.
 *
 * `onUnauthorized` is registered exactly once, here, and points at `logout()` — the
 * LOCAL sign-out. When the HTTP client gives up on a dead refresh token it fires this,
 * and the store flipping to `anonymous` swaps the whole stack back to the login screen.
 *
 * `onMustChangePassword` is the same shape of seam for the same reason: the HTTP client
 * is the only code that sees a token refresh, and the refresh is the only place a
 * long-running session learns that an administrator has reset its password. It flips
 * the store, and the store swaps the stack — the client never navigates.
 *
 * The query cache is PERSISTED to AsyncStorage so a cold start — or a phone with no
 * signal on-site — shows the last-known dashboard, attendance and lists instantly
 * instead of a spinner. Two rules guard it:
 *  - Tokens never touch this store: they live in the Keychain (see shared/api/tokens).
 *    Only query DATA is persisted here.
 *  - Faces never touch disk: the personnel DETAIL query (queryKey[0] === 'personnel')
 *    carries `avatar_base64`, so it is excluded from dehydration. Lists, summaries and
 *    attendance hold no avatars and persist freely.
 *  - Pay never touches disk either. `salary` and `payroll` queries are excluded for the
 *    same reason the `salaries` permission exists at all: an oklad is need-to-know, and
 *    AsyncStorage is a plaintext file on a device that gets handed around. The cost is
 *    one request on a cold start; the alternative is a payroll that outlives the
 *    session that was allowed to read it.
 */
const CACHE_MAX_AGE = 24 * 60 * 60 * 1000; // 24h

/**
 * Что мутация просит сказать вслух.
 *
 * Половина действий кабинета заканчивается тем, что лист закрылся, а строка исчезла, —
 * и до сих пор про них не говорилось НИЧЕГО. Объявление рядом с самой мутацией
 * (`meta: {toast: 'ключ'}`) — это одна строка на месте действия и ноль повторяющегося
 * кода в каждом `onSuccess`.
 *
 * Ошибки по умолчанию МОЛЧАТ, и это осознанно: почти всякая форма здесь уже печатает
 * свою ошибку баннером рядом с полем, где её и надо читать. `toastError` включается
 * там, где такого места нет — команда терминалу, действие из закрывшегося листа.
 */
type ToastMeta = {
  /** Ключ i18n для сообщения об успехе. */
  toast?: string;
  /** Сообщать об ошибке тостом — для действий без своего места под баннер. */
  toastError?: boolean;
};

function toastMeta(meta: unknown): ToastMeta {
  return (meta ?? {}) as ToastMeta;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A phone loses signal constantly; hammering a failed call helps nobody.
      retry: 1,
      staleTime: 30_000,
      // Must be >= the persister's maxAge, or GC drops data before restore can use it.
      gcTime: CACHE_MAX_AGE,
    },
  },
  mutationCache: new MutationCache({
    onSuccess: (_data, _vars, _ctx, mutation) => {
      const key = toastMeta(mutation.options.meta).toast;
      if (key) {
        toast.success(i18n.t(key));
      }
    },
    onError: (error, _vars, _ctx, mutation) => {
      if (toastMeta(mutation.options.meta).toastError) {
        toast.error(messageOf(error, i18n.t('errors.generic')));
      }
    },
  }),
});

/**
 * Query families that must not be written to AsyncStorage — see the note above. Keyed
 * on `queryKey[0]`, so every caller in these families has to keep that first segment.
 */
const NEVER_PERSISTED: ReadonlySet<unknown> = new Set([
  'personnel',
  'salary',
  'payroll',
]);

const persister = createAsyncStoragePersister({
  storage: AsyncStorage,
  key: 'svq-cache',
});

const persistOptions = {
  persister,
  maxAge: CACHE_MAX_AGE,
  buster: CACHE_VERSION,
  dehydrateOptions: {
    shouldDehydrateQuery: (query: {state: {status: string}; queryKey: readonly unknown[]}) =>
      query.state.status === 'success' && !NEVER_PERSISTED.has(query.queryKey[0]),
  },
};

export function Providers({children}: {children: React.ReactNode}) {
  useEffect(() => {
    onUnauthorized(() => {
      void useAuthStore.getState().logout();
    });
    onMustChangePassword(() => {
      useAuthStore.getState().requirePasswordChange();
    });
    void applyStoredLang();
  }, []);

  return (
    <SafeAreaProvider>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={persistOptions}>
        {children}
      </PersistQueryClientProvider>
    </SafeAreaProvider>
  );
}
